import {
  actionsToVector,
  ConsoleBootstrapSchema,
  type CameraName,
  type ConsoleBootstrap,
  type PublicConfig,
} from '@digger/shared';
import { parseDriveSession, showDriveSessionError, startDriveSessionUi } from './drive-session.js';
import { Hud } from './hud.js';
import { KeyboardTracker } from './keyboard.js';
import { RttTracker, VideoStatsPoller } from './latency.js';
import { createTokenProvider } from './login.js';
import { PoseInset } from './pose-inset.js';
import { SystemPanel } from './system-panel.js';
import { ConsoleState } from './state.js';
import { ControlTicker } from './ticker.js';
import { WebRtcTransport, WsControlTransport, type ControlTransport } from './transport.js';
import { VideoPanels } from './video.js';

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing element #${id}`);
  return el as T;
};

/**
 * /api/config from whoever serves us: the Pi agent (LAN — the full
 * PublicConfig) or the signaling relay (internet — a login bootstrap; the
 * real config arrives over signaling once the agent accepts us).
 */
async function fetchBootstrap(): Promise<ConsoleBootstrap> {
  while (true) {
    try {
      const res = await fetch('/api/config');
      if (res.ok) {
        const raw = (await res.json()) as Record<string, unknown>;
        raw.mode ??= 'agent'; // pre-internet-release agents send neither…
        raw.authRequired ??= false; // …field; both default to the old behavior
        return ConsoleBootstrapSchema.parse(raw);
      }
    } catch {
      /* host not up yet */
    }
    $('hud-connection').textContent = 'link: waiting for /api/config…';
    await new Promise((r) => setTimeout(r, 1000));
  }
}

async function main(): Promise<void> {
  // Booked drive session? The platform hands us a slot-scoped JWT in the
  // hash (never sent to the server); strip it from the URL immediately.
  const drive = parseDriveSession(window.location.hash);
  if (drive) history.replaceState(null, '', window.location.pathname + window.location.search);

  const bootstrap = await fetchBootstrap();
  const tokens = createTokenProvider(
    bootstrap,
    drive && {
      token: drive.token,
      onInvalid: () =>
        showDriveSessionError(
          'The digger rejected your session token — your slot may be over.',
          drive.returnUrl,
        ),
    },
  );
  const relayMode = bootstrap.mode === 'relay';

  const state = new ConsoleState();
  const hud = new Hud({
    connection: $('hud-connection'),
    controlRtt: $('hud-control-rtt'),
    videoLatency: $('hud-video-latency'),
    dominant: $('hud-dominant'),
    vector: $('hud-vector'),
    estopBanner: $('banner-estop'),
    staleBanner: $('banner-stale'),
  });
  const panels = new VideoPanels($('video-front'), $('video-rear'));
  const rtt = new RttTracker();
  const systemPanel = new SystemPanel($('system-panel'), relayMode ? 'push' : 'poll');

  let controlOpen = false;
  let connectionLabel = 'disconnected';
  let webrtc: WebRtcTransport | undefined;
  const poseInset = new PoseInset(
    $('pose-inset'),
    relayMode ? () => webrtc?.sendPoseReset() : undefined,
  );

  // Relay mode learns the agent's config over signaling; agent (LAN) mode
  // already holds it (bootstrap IS the config) but receives the same message
  // anyway — first one wins.
  let resolveConfig: ((cfg: PublicConfig) => void) | undefined;
  const configReady = new Promise<PublicConfig>((r) => (resolveConfig = r));

  const events = {
    onState: (s: string) => {
      connectionLabel = s;
      hud.setConnection(s, controlOpen);
    },
    onTrack: (camera: CameraName, stream: MediaStream) => {
      panels.attachStream(camera, stream);
    },
    onAck: (ack: Parameters<RttTracker['onAck']>[0]) => {
      rtt.onAck(ack);
      if (ack.pose) poseInset.update(ack.pose);
    },
    onControlOpen: () => {
      controlOpen = true;
      hud.setConnection(connectionLabel, true);
    },
    onControlClosed: () => {
      controlOpen = false;
      rtt.reset();
      hud.setConnection(connectionLabel, false);
    },
    onDisplaced: () => hud.setDisplaced(),
    onConfig: (cfg: PublicConfig) => {
      resolveConfig?.(cfg);
      resolveConfig = undefined;
    },
    onSystem: (stats: Parameters<SystemPanel['feed']>[0]) => systemPanel.feed(stats),
  };

  let transport: ControlTransport;
  if (relayMode || bootstrap.transport === 'webrtc') {
    webrtc = new WebRtcTransport(events, {
      initialConfig: relayMode ? undefined : bootstrap,
      getToken: tokens ? () => tokens.getToken() : undefined,
      onAuthRejected: tokens ? () => tokens.invalidate() : undefined,
    });
    transport = webrtc;
  } else {
    // MJPEG bring-up mode (LAN/debug only): video via <img>, control via WS.
    transport = new WsControlTransport(events);
  }

  transport.start();
  const cfg = relayMode ? await configReady : (bootstrap as PublicConfig);

  if (!relayMode && cfg.transport === 'mjpeg-ws') {
    const stage = $('stage');
    for (const camera of ['front', 'rear'] as const) {
      if (cfg.cameras[camera].enabled) panels.attachMjpeg(camera, stage);
    }
  }

  const statsPoller = new VideoStatsPoller(cfg.captureEncodeMs, () => webrtc?.cameraMids ?? {});

  // --- keyboard + safety -----------------------------------------------------
  const estopNow = () => {
    state.latchEstop();
    hud.setEstop(true);
    keyboard.clear();
    ticker.tick(); // don't wait for the next tick to tell the agent
  };
  const estopReset = () => {
    state.resetEstop();
    hud.setEstop(false);
  };

  const keyboard = new KeyboardTracker({
    onEstop: estopNow,
    onEstopReset: estopReset,
    onSwap: () => {
      hud.setDominant(state.swapCamera());
      panels.setDominant(state.dominant);
    },
  });
  keyboard.attach(window);

  // blur() after click so no button keeps focus: a focused button turns the
  // next Space press into a click on itself instead of an E-STOP.
  const btnEstop = $('btn-estop');
  const btnReset = $('btn-reset');
  btnEstop.addEventListener('click', () => {
    estopNow();
    btnEstop.blur();
  });
  btnReset.addEventListener('click', () => {
    estopReset();
    btnReset.blur();
  });

  // --- fixed-rate control loop -------------------------------------------------
  const ticker = new ControlTicker({
    tickHz: cfg.controlTickHz,
    sampleActions: () => keyboard.actions(),
    sampleEstop: () => state.estopLatched,
    send: (frame) => {
      const sent = transport.sendFrame(frame);
      hud.setVector(actionsToVector(keyboard.actions()), sent);
    },
  });
  ticker.start();

  hud.setDominant(state.dominant);
  panels.setDominant(state.dominant);
  hud.setEstop(false);

  // Booked slots end themselves: countdown chip, 5-minute warning, then
  // E-STOP + handover overlay + redirect back to the portal. The JWT expires
  // at the same instant, so this is UX — the agent is the enforcement.
  if (drive?.untilMs) startDriveSessionUi(drive, estopNow);

  // --- HUD refresh ---------------------------------------------------------
  setInterval(() => {
    hud.setControlRtt(rtt, cfg.deadmanMs);
  }, 250);

  setInterval(() => {
    const pc = webrtc?.peerConnection;
    if (pc) {
      void statsPoller.poll(pc).then((report) => hud.setVideoLatency(report, state.dominant));
    }
  }, 1000);
}

void main();
